const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const firstNumber = (...values) => {
  const match = values.find((value) => Number.isFinite(Number(value)));
  return toFiniteNumber(match);
};

const toDistribution = (distribution) => {
  const entries = Array.isArray(distribution)
    ? distribution.map((item = {}) => [item.rank || item.country || item.label, item])
    : Object.entries(distribution || {});
  const total = entries.reduce((sum, [, value]) => (
    sum + toFiniteNumber(typeof value === "object" ? value.count : value)
  ), 0);

  return entries
    .map(([label, value]) => {
      const count = toFiniteNumber(typeof value === "object" ? value.count : value);
      const suppliedPercentage = typeof value === "object" ? value.percentage : undefined;
      return {
        rank: String(label || "Necunoscut"),
        count,
        percentage: Number.isFinite(Number(suppliedPercentage))
          ? toFiniteNumber(suppliedPercentage)
          : (total > 0 ? Math.round((count / total) * 100) : 0),
      };
    })
    .filter((item) => item.count > 0);
};

const toCountryDistribution = (distribution) =>
  toDistribution(distribution).map(({ rank, ...item }) => ({
    ...item,
    country: rank,
  }));

const toPopularVideos = (videos = []) =>
  (Array.isArray(videos) ? videos : []).map((video = {}, index) => ({
    videoId: video.videoId || video.id || `video-${index + 1}`,
    title: video.title || `Video ${index + 1}`,
    views: firstNumber(video.views, video.viewCount),
    rank: firstNumber(video.rank, index + 1),
  }));

export const fetchAdminDashboardData = async (service, timeRange, adminEmail) => {
  try {
    const analytics = await service.getAdvancedAnalytics(timeRange);
    return { analytics, source: "advanced" };
  } catch {
    const analytics = await service.getAnalytics(timeRange, adminEmail);
    return { analytics, source: "legacy" };
  }
};

export const normalizeAdminDashboardData = (response = {}) => {
  const data = response?.data || response || {};
  const basic = data.basicMetrics || {};
  const legacyOverview = data.overview || {};
  const legacyUsers = data.users || {};
  const legacyVideos = data.videos || {};
  const legacyEngagement = data.engagement || {};
  const legacyPerformance = data.performance || {};

  const rankDistribution = legacyUsers.byRank
    || data.userMetrics?.rankDistribution
    || data.userDistribution?.byRank;
  const countryDistribution = legacyUsers.byCountry
    || data.userMetrics?.countryDistribution
    || data.userDistribution?.byCountry;
  const popularVideos = legacyVideos.popularVideos
    || data.detailedData?.topVideos
    || data.popularContent?.topVideos;

  return {
    users: {
      total: firstNumber(legacyUsers.total, basic.totalUsers, legacyOverview.totalUsers),
      active: firstNumber(legacyUsers.active, basic.activeUsers, legacyOverview.activeUsers),
      byRank: toDistribution(rankDistribution),
      byCountry: toCountryDistribution(countryDistribution),
    },
    videos: {
      total: firstNumber(legacyVideos.total, basic.totalVideos, legacyOverview.totalVideos),
      totalViews: firstNumber(legacyVideos.totalViews, basic.totalViews, legacyOverview.totalViews),
      totalWatchTime: firstNumber(legacyVideos.totalWatchTime, basic.totalWatchTime, legacyOverview.totalWatchTime),
      popularVideos: toPopularVideos(popularVideos),
    },
    engagement: {
      totalCertificates: firstNumber(
        legacyEngagement.totalCertificates,
        basic.completedVideos,
        legacyOverview.certificatesEarned
      ),
      newFavorites: firstNumber(
        legacyEngagement.newFavorites,
        basic.totalFavorites,
        legacyOverview.newFavorites
      ),
    },
    performance: {
      completionRate: firstNumber(
        legacyPerformance.completionRate,
        basic.completionRate,
        legacyOverview.completionRate
      ),
      engagementRate: firstNumber(
        legacyPerformance.engagementRate,
        basic.engagementRate,
        legacyOverview.engagementRate
      ),
    },
    recentActivities: Array.isArray(data.recentActivities)
      ? data.recentActivities
      : [],
    activityTimeline: Array.isArray(data.activityMetrics?.dailyActivity)
      ? data.activityMetrics.dailyActivity
      : [],
    overview: {
      avgSessionTime: legacyOverview.avgSessionTime || legacyOverview.averageWatchTime || "N/A",
      storageUsed: legacyOverview.storageUsed || "N/A",
    },
  };
};
